import React, { useCallback, useState, useEffect } from 'react';
import { useDropzone } from 'react-dropzone';
import PropTypes from 'prop-types';
export function QuotePdfUploader({ quote, saveDocument, addDocument, allowedFileTypes, maxFiles }) {
    const [uploadedFiles, setUploadedFiles] = useState([]);
    const [error, setError] = useState('');
    const [validFilesList, setValidFilesList] = useState([]); 
    const defaultFileExtensions = ['.pdf', '.rfa', '.rvt', '.cmdrw', '.cmfav', '.dwg', '.xls', '.xlsx'];

    const validFileExtensions = Array.isArray(allowedFileTypes) && allowedFileTypes.length > 0
        ? allowedFileTypes
        : defaultFileExtensions;

    const isLimited = typeof maxFiles === 'number' && maxFiles > 0;

    const onDrop = useCallback(
        (acceptedFiles) => {
            if (isLimited && uploadedFiles.length >= maxFiles) {
                setError(`You can only upload up to ${maxFiles} file(s).`);
                return;
            }

            const validFiles = acceptedFiles.filter((file) =>
                validFileExtensions.some((ext) => file.name.toLowerCase().endsWith(ext))
            );

            if (validFiles.length < acceptedFiles.length) {
                setError('Unsupported file type. Only PDF and Excel files are allowed.');
            } else {
                setError('');
                const newFiles = validFiles.map((file) => ({
                    fileName: file.name,
                    fileSize: file.size,
                    title: file.name,
                    id: Math.floor(Math.random() * 100000),
                    quoteId: quote.id
                }));

                let combinedFiles = [...uploadedFiles, ...newFiles];
                if (isLimited) {
                    combinedFiles = combinedFiles.slice(0, maxFiles);
                }

                setUploadedFiles(combinedFiles);
                saveDocument(combinedFiles);

                let updatedValidFiles = [...validFilesList, ...validFiles];
                if (isLimited) {
                    updatedValidFiles = updatedValidFiles.slice(0, maxFiles);
                }

                setValidFilesList(updatedValidFiles);
                addDocument(updatedValidFiles);
            }
        },
        [validFileExtensions, quote.id, uploadedFiles, saveDocument, maxFiles, validFilesList, addDocument, isLimited]
    );

    const deleteFile = (index) => {
        const updatedFiles = [...uploadedFiles];
        updatedFiles.splice(index, 1);
        setUploadedFiles(updatedFiles);
        saveDocument(updatedFiles);

        const updatedValidFiles = [...validFilesList];
        updatedValidFiles.splice(index, 1);
        setValidFilesList(updatedValidFiles);
        addDocument(updatedValidFiles);
    };

    const { getRootProps, getInputProps } = useDropzone({
        onDrop,
        accept: validFileExtensions.map((ext) => `application/${ext.replace('.', '')}`).join(','),
        multiple: !isLimited
    });

    useEffect(() => {
        if (quote.documents) {
            setUploadedFiles(quote.documents);
        }
    }, [quote.documents]);

    return (
        <div className="admin-sortable-wrap">
            {error && <p className="error-message">{error}</p>}
            <div className="admin-sortable-file">
                <ul>
                    {uploadedFiles.map((file, index) => (
                        <li key={index}>
                            <span className="admin-sortable-file-name">{file.fileName}</span>
                            <button
                                className="admin-sortable-file-delete"
                                onClick={() => deleteFile(index)}
                            >
                                <span className="material-symbols-rounded">close</span>
                            </button>
                        </li>
                    ))}
                </ul>
            </div>

            {(!isLimited || uploadedFiles.length < maxFiles) && (
                <div {...getRootProps()} className="dropzone">
                    <input {...getInputProps()} />
                    <button className="admin-small-upload-button">Upload PDF/Excel</button>
                </div>
            )}
        </div>
    );
}

QuotePdfUploader.propTypes = {
    quote: PropTypes.object.isRequired,
    saveDocument: PropTypes.func.isRequired,
    addDocument: PropTypes.func.isRequired,
    allowedFileTypes: PropTypes.arrayOf(PropTypes.string),
    maxFiles: PropTypes.number
};

export default QuotePdfUploader;

